/**
 * Member profile route (Organizers only)
 * View and manage member details, constraints, and exclusions
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { useUser, useUpdateUser } from '@/hooks/useUsers';
import {
  useConstraints,
  useExclusions,
  useCreateConstraint,
  useDeleteConstraint,
  useCreateMealExclusion,
  useDeleteMealExclusion,
} from '@/hooks/useConstraints';
import { useMeals } from '@/hooks/useMeals';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { Loading } from '@/components/ui/Loading';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { useToast } from '@/components/ui/ToastProvider';
import { DelegationSection } from '@/components/users/DelegationSection';
import { useAuth } from '@/hooks/useAuth';
import type { ConstraintType, ConstraintSeverity, Constraint, MealExclusion, Meal } from '@/types/api';

export const Route = createFileRoute('/users/$id')({
  beforeLoad: requireOrganizer,
  component: UserDetailComponent,
});

// Generate a consistent color from a string (user name)
function getAvatarColor(name: string): string {
  const colors = [
    '#5B4FE9', // Purple
    '#10B981', // Green
    '#F59E0B', // Amber
    '#EF4444', // Red
    '#3B82F6', // Blue
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#14B8A6', // Teal
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
}

// Get initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Get constraint type styling
function getConstraintStyle(type: string, severity: string): { bg: string; border: string; text: string } {
  if (severity === 'strict') {
    switch (type) {
      case 'allergy':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' };
      case 'medical':
        return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' };
      default:
        return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' };
    }
  }
  switch (type) {
    case 'dislike':
      return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800' };
    case 'diet':
      return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' };
    default:
      return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' };
  }
}

function getConstraintBadgeStyle(type: string): { bg: string; text: string } {
  switch (type) {
    case 'allergy':
      return { bg: 'bg-red-100', text: 'text-red-800' };
    case 'dislike':
      return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
    case 'diet':
      return { bg: 'bg-green-100', text: 'text-green-800' };
    case 'medical':
      return { bg: 'bg-purple-100', text: 'text-purple-800' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800' };
  }
}

function getSeverityBadgeStyle(severity: string): { bg: string; text: string } {
  if (severity === 'strict') {
    return { bg: 'bg-red-100', text: 'text-red-700' };
  }
  return { bg: 'bg-blue-100', text: 'text-blue-700' };
}

interface ConstraintItemProps {
  constraint: Constraint;
  onDelete: () => void;
  isDeleting: boolean;
}

function ConstraintItem({ constraint, onDelete, isDeleting }: ConstraintItemProps) {
  const { t } = useTranslation();
  const style = getConstraintStyle(constraint.constraintType, constraint.severity);
  const badgeStyle = getConstraintBadgeStyle(constraint.constraintType);
  const severityStyle = getSeverityBadgeStyle(constraint.severity);

  return (
    <div className={`p-4 rounded-lg border ${style.bg} ${style.border}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeStyle.bg} ${badgeStyle.text}`}>
              {t(`constraint.${constraint.constraintType}`)}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severityStyle.bg} ${severityStyle.text}`}>
              {t(`constraint.${constraint.severity}`)}
            </span>
          </div>
          <p className={`font-medium ${style.text}`}>{constraint.value}</p>
        </div>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="ml-3 text-gray-400 hover:text-red-500 disabled:opacity-50"
          title={t('common.remove')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

interface ExclusionItemProps {
  exclusion: MealExclusion;
  onDelete: () => void;
  isDeleting: boolean;
}

function ExclusionItem({ exclusion, onDelete, isDeleting }: ExclusionItemProps) {
  const { t } = useTranslation();

  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-white">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="font-medium text-gray-900">{exclusion.mealName}</p>
          {exclusion.reason && (
            <p className="text-sm text-gray-500 mt-1">{exclusion.reason}</p>
          )}
          {exclusion.mealTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {exclusion.mealTags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={onDelete}
          disabled={isDeleting}
          className="ml-3 text-gray-400 hover:text-red-500 disabled:opacity-50"
          title={t('common.remove')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

interface AddConstraintFormProps {
  userId: number;
  onClose: () => void;
  onSuccess: () => void;
}

function AddConstraintForm({ userId, onClose, onSuccess }: AddConstraintFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const createConstraint = useCreateConstraint();

  const form = useForm({
    defaultValues: {
      constraintType: 'dislike' as ConstraintType,
      value: '',
      severity: 'preference' as ConstraintSeverity,
    },
    onSubmit: async ({ value }) => {
      try {
        await createConstraint.mutateAsync({
          userId,
          constraintType: value.constraintType,
          value: value.value,
          severity: value.severity,
        });
        toast.success(t('users.constraintAdded'));
        form.reset();
        onClose();
        onSuccess();
      } catch {
        toast.error(t('users.errorAddingConstraint'));
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="p-4 border border-gray-200 rounded-lg bg-gray-50"
    >
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('constraint.constraintType')}
          </label>
          <form.Field
            name="constraintType"
            children={(field) => (
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value as ConstraintType)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="allergy">{t('constraint.allergy')}</option>
                <option value="dislike">{t('constraint.dislike')}</option>
                <option value="diet">{t('constraint.diet')}</option>
                <option value="medical">{t('constraint.medical')}</option>
              </select>
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('constraint.value')}
          </label>
          <form.Field
            name="value"
            children={(field) => (
              <input
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('constraint.valuePlaceholder')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('constraint.severity')}
          </label>
          <form.Field
            name="severity"
            children={(field) => (
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value as ConstraintSeverity)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              >
                <option value="strict">{t('constraint.strict')}</option>
                <option value="preference">{t('constraint.preference')}</option>
              </select>
            )}
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={createConstraint.isPending}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {createConstraint.isPending ? t('common.adding') : t('common.add')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('app.cancel')}
          </button>
        </div>
      </div>
    </form>
  );
}

interface AddExclusionFormProps {
  userId: number;
  onClose: () => void;
  onSuccess: () => void;
}

function AddExclusionForm({ userId, onClose, onSuccess }: AddExclusionFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
  const [reason, setReason] = useState('');
  const createExclusion = useCreateMealExclusion();
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: mealsData } = useMeals(
    { query: debouncedSearch, limit: 10 },
    { enabled: debouncedSearch.length > 0 },
  );

  const filteredMeals = debouncedSearch ? (mealsData?.meals ?? []) : [];

  const handleSubmit = async () => {
    if (!selectedMeal) return;

    try {
      await createExclusion.mutateAsync({
        userId,
        mealId: selectedMeal.id,
        reason: reason || undefined,
      });
      toast.success(t('users.exclusionAdded'));
      setSelectedMeal(null);
      setReason('');
      setSearchQuery('');
      onClose();
      onSuccess();
    } catch {
      toast.error(t('users.errorAddingExclusion'));
    }
  };

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="space-y-3">
        {!selectedMeal ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.selectMeal')}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('users.searchMeals')}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            />
            {filteredMeals.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-md bg-white max-h-40 overflow-y-auto">
                {filteredMeals.map((meal) => (
                  <button
                    key={meal.id}
                    type="button"
                    onClick={() => {
                      setSelectedMeal(meal);
                      setSearchQuery('');
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    {meal.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between p-2 bg-white rounded border border-gray-200">
              <span className="font-medium text-gray-900">{selectedMeal.name}</span>
              <button
                type="button"
                onClick={() => setSelectedMeal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('constraint.reason')}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('constraint.reasonPlaceholder')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={createExclusion.isPending}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {createExclusion.isPending ? t('common.adding') : t('common.add')}
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  setSelectedMeal(null);
                  setReason('');
                }}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t('app.cancel')}
              </button>
            </div>
          </>
        )}

        {!selectedMeal && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t('app.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function UserDetailComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const userId = parseInt(id, 10);
  const { data: user, isLoading, error, refetch: refetchUser } = useUser(userId);
  const { data: constraints = [], refetch: refetchConstraints } = useConstraints(userId);
  const { data: exclusions = [], refetch: refetchExclusions } = useExclusions(userId);
  const updateMutation = useUpdateUser();
  const deleteConstraint = useDeleteConstraint();
  const deleteExclusion = useDeleteMealExclusion();
  const { user: currentUser } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [deletingConstraintId, setDeletingConstraintId] = useState<number | null>(null);
  const [deletingExclusionId, setDeletingExclusionId] = useState<number | null>(null);
  const [isConstraintFormOpen, setIsConstraintFormOpen] = useState(false);
  const [isExclusionFormOpen, setIsExclusionFormOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      isOrganizer: user?.isOrganizer ?? false,
      defaultAvailability: user?.defaultAvailability ?? false,
    },
    onSubmit: async ({ value }) => {
      try {
        await updateMutation.mutateAsync({
          id: userId,
          data: {
            isOrganizer: value.isOrganizer,
            defaultAvailability: value.defaultAvailability,
          },
        });
        toast.success(t('users.updateSuccess'));
        setIsEditing(false);
        refetchUser();
      } catch {
        toast.error(t('users.updateError'));
      }
    },
  });

  // Update form when user data changes
  if (user && (form.state.values.isOrganizer !== user.isOrganizer || form.state.values.defaultAvailability !== user.defaultAvailability)) {
    form.setFieldValue('isOrganizer', user.isOrganizer);
    form.setFieldValue('defaultAvailability', user.defaultAvailability);
  }

  const handleDeleteConstraint = async (constraintId: number) => {
    setDeletingConstraintId(constraintId);
    try {
      await deleteConstraint.mutateAsync(constraintId);
      toast.success(t('users.constraintDeleted'));
      refetchConstraints();
    } catch {
      toast.error(t('users.errorDeletingConstraint'));
    } finally {
      setDeletingConstraintId(null);
    }
  };

  const handleDeleteExclusion = async (exclusionId: number) => {
    setDeletingExclusionId(exclusionId);
    try {
      await deleteExclusion.mutateAsync(exclusionId);
      toast.success(t('users.exclusionDeleted'));
      refetchExclusions();
    } catch {
      toast.error(t('users.errorDeletingExclusion'));
    } finally {
      setDeletingExclusionId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate({ to: '/users' })}
            className="mb-4 text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            ← {t('users.backToMembers')}
          </button>
          <ErrorMessage
            title={t('users.notFound')}
            message={t('users.userNotFoundDescription')}
          />
        </div>
      </div>
    );
  }

  const avatarColor = getAvatarColor(user.name);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back link */}
        <button
          onClick={() => navigate({ to: '/users' })}
          className="mb-6 text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          ← {t('users.backToMembers')}
        </button>

        {/* Profile Header */}
        <div className="flex items-center gap-4 mb-8">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: avatarColor }}
          >
            {getInitials(user.name)}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">{user.slug}</span>
              <span className="text-gray-300">|</span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  user.isOrganizer
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {user.isOrganizer ? t('users.organizer') : t('users.member')}
              </span>
            </div>
          </div>
        </div>

        {/* Stacked Layout */}
        <div className="space-y-6">
          {/* Constraints */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">
                {t('users.constraints')}
              </h2>
              {!isConstraintFormOpen && (
                <button
                  onClick={() => setIsConstraintFormOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                >
                  + {t('users.addConstraint')}
                </button>
              )}
            </div>

            {isConstraintFormOpen && (
              <div className="mb-4">
                <AddConstraintForm
                  userId={userId}
                  onClose={() => setIsConstraintFormOpen(false)}
                  onSuccess={() => refetchConstraints()}
                />
              </div>
            )}

            {constraints.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">{t('users.noConstraints')}</p>
            ) : (
              <div className="space-y-3">
                {constraints.map((constraint) => (
                  <ConstraintItem
                    key={constraint.id}
                    constraint={constraint}
                    onDelete={() => handleDeleteConstraint(constraint.id)}
                    isDeleting={deletingConstraintId === constraint.id}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Exclusions */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900">
                {t('users.exclusions')}
              </h2>
              {!isExclusionFormOpen && (
                <button
                  onClick={() => setIsExclusionFormOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100"
                >
                  + {t('users.addExclusion')}
                </button>
              )}
            </div>

            {isExclusionFormOpen && (
              <div className="mb-4">
                <AddExclusionForm
                  userId={userId}
                  onClose={() => setIsExclusionFormOpen(false)}
                  onSuccess={() => refetchExclusions()}
                />
              </div>
            )}

            {exclusions.length === 0 ? (
              <p className="text-sm text-gray-500 py-4">{t('users.noExclusions')}</p>
            ) : (
              <div className="space-y-3">
                {exclusions.map((exclusion) => (
                  <ExclusionItem
                    key={exclusion.id}
                    exclusion={exclusion}
                    onDelete={() => handleDeleteExclusion(exclusion.id)}
                    isDeleting={deletingExclusionId === exclusion.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Settings Section */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">
            {t('users.userDetails')}
          </h2>

          <div className="space-y-4">
            {/* Role */}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-600">{t('users.role')}</span>
              {isEditing ? (
                <form.Field
                  name="isOrganizer"
                  children={(field) => (
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        className="rounded border-gray-300 text-purple-600 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {t('users.makeOrganizer')}
                      </span>
                    </label>
                  )}
                />
              ) : (
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    user.isOrganizer
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {user.isOrganizer ? t('users.organizer') : t('users.member')}
                </span>
              )}
            </div>

            {/* Default Availability */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <span className="text-sm text-gray-600">{t('users.defaultAvailability')}</span>
              {isEditing ? (
                <form.Field
                  name="defaultAvailability"
                  children={(field) => (
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={field.state.value}
                        onChange={(e) => field.handleChange(e.target.checked)}
                        className="rounded border-gray-300 text-green-600 shadow-sm focus:border-green-500 focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {t('users.enableDefaultAvailability')}
                      </span>
                    </label>
                  )}
                />
              ) : (
                <span className={`text-sm ${user.defaultAvailability ? 'text-green-600' : 'text-gray-500'}`}>
                  {user.defaultAvailability ? t('common.yes') : t('common.no')}
                </span>
              )}
            </div>

            {/* Created At */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <span className="text-sm text-gray-600">{t('users.createdAt')}</span>
              <span className="text-sm text-gray-900">
                {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>

            {/* Edit Button */}
            <div className="pt-4 border-t border-gray-100">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  {t('users.edit')}
                </button>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => form.handleSubmit()}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? t('app.saving') : t('app.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      form.reset();
                      setIsEditing(false);
                    }}
                    disabled={updateMutation.isPending}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('app.cancel')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delegation section - only for organizers */}
        {currentUser?.isOrganizer && (
          <div className="mt-6">
            <DelegationSection user={user} />
          </div>
        )}
      </div>
    </div>
  );
}
